import Close from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { JobStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { AssignJobToView } from '~components/Job/AssignJobToView/AssignJobToView';
import {
  OverflowAwareText,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Account } from '~hooks/useAccount/models';
import { Job } from '~hooks/useJob/models';
import { User } from '~hooks/useUsers/models';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { canAcceptJob } from '../utils';

interface JobAssigneeHandlerProps {
  filterDate?: string;
  job: Job;
  onAssigneeChange: (job: Job) => void;
  onAssigneeChangeFailed?: () => void;
  onLoadingStatusChange?: (isLoading: boolean) => void;
}

export default function JobAssigneeHandler({
  filterDate,
  job,
  onAssigneeChange,
  onAssigneeChangeFailed,
  onLoadingStatusChange,
}: JobAssigneeHandlerProps) {
  const {
    id,
    driverOrVendorAssignable,
    editable,
    driver,
    status,
    vendorJobAssignment,
    stateEvents,
  } = job;

  const { userStore } = useStores();

  const companyId = getEffectiveUserCompanyId(userStore);

  const vendorName = vendorJobAssignment?.vendorAccount?.name;
  const driverName = driver?.fullName;
  const hasVendorOrDriver = Boolean(vendorName || driverName);

  const isAcceptable = canAcceptJob(job);
  const isEditable = Boolean(editable && hasVendorOrDriver);
  const isUnassignable = stateEvents.includes(JobStateEvent.UNASSIGN);

  if (isAcceptable) {
    return null;
  }

  return (
    <AssignJobToView
      companyId={companyId}
      filterDate={filterDate}
      driver={(driver as unknown as User) ?? undefined}
      driverOrVendorAssignable={driverOrVendorAssignable}
      editable={isEditable}
      isUnassignable={isUnassignable}
      jobId={id}
      onSuccess={onAssigneeChange}
      onError={onAssigneeChangeFailed}
      status={status}
      valueToDisplay={driverName ? 'driver' : 'vendor'}
      vendor={(vendorJobAssignment?.vendorAccount as Account) ?? undefined}
      customAssignCTA={
        <SmallButton
          color="brandV2Orange"
          variant="text"
          sx={{
            '&.MuiButtonBase-root': {
              borderStyle: 'dashed',
              borderWidth: '1px',
              '&:not(:hover)': { backgroundColor: 'background.paper' },
            },
          }}
        >
          <OverflowAwareText>{t('actions.assign')}</OverflowAwareText>
        </SmallButton>
      }
      customUnassignCTA={
        isUnassignable ? (
          <IconButton
            size="small"
            sx={{ width: '20px', height: '20px' }}
            data-ui={`page-dispatch__job-assignee__job-${job.jobId}__unassign-button`}
          >
            <Close sx={{ width: '12px', height: '12px' }} />
          </IconButton>
        ) : undefined
      }
      onLoadingStatusChange={onLoadingStatusChange}
    />
  );
}
